/**
 * ダッシュボード情報APIサービス
 * バックエンドAPIと通信してダッシュボードデータを取得
 */

import { apiClient } from '@/shared/lib/axios';
import { logger } from '@/shared/lib/logger';
import { PanelId } from '@/domain/value-objects/PanelId';
import { PanelStatus } from '@/domain/value-objects/PanelStatus';
import { LogLevel } from '@/domain/value-objects/LogLevel';
import type {
  DashboardData,
  PanelInfo,
  PanelStatusLogEntry,
  PanelStatusLogEntryApiResponse,
  PanelStatusLogData,
  SystemLogEntry,
} from '../types/dashboard.types';
import { DateFactory } from '@/shared/utils/dateFactory';

// 表示上限定数
const MAX_PANEL_DISPLAY_COUNT = 100;
const MAX_LOG_DISPLAY_COUNT = 100;

/**
 * APIレスポンス型定義
 */
interface DashboardApiResponse {
  readonly success: boolean;
  readonly data: {
    readonly statistics: {
      readonly totalPanelCount: number;
      readonly activePanelCount: number;
      readonly stoppedPanelCount: number;
      readonly warningCount: number;
    };
    readonly panelStatusGrid: {
      readonly panels: Array<{
        readonly panelId: number;
        readonly panelName: string;
        readonly status: 'active' | 'stopped' | 'warning';
        readonly lastCommunication: string | null;
      }>;
    };
    readonly systemLog: {
      readonly logs: Array<{
        readonly logId: string;
        readonly timestamp: string;
        readonly level: 'info' | 'warning' | 'error';
        readonly message: string;
        readonly source: string | null;
      }>;
    };
  };
}

interface StatisticsApiResponse {
  readonly success: boolean;
  readonly statistics: {
    readonly totalPanelCount: number;
    readonly activePanelCount: number;
    readonly stoppedPanelCount: number;
    readonly warningCount: number;
  };
}

interface PanelStatusApiResponse {
  readonly success: boolean;
  readonly panels: Array<{
    readonly panelId: number;
    readonly panelName: string;
    readonly status: 'active' | 'stopped' | 'warning';
    readonly lastCommunication: string | null;
  }>;
}

interface SystemLogsApiResponse {
  readonly success: boolean;
  readonly logs: Array<{
    readonly logId: string;
    readonly timestamp: string;
    readonly level: 'info' | 'warning' | 'error';
    readonly message: string;
    readonly source: string | null;
  }>;
}

interface RecentStatusLogsApiResponse {
  readonly success: boolean;
  readonly logs: readonly PanelStatusLogEntryApiResponse[];
}

interface StatusLogsApiResponse {
  readonly success: boolean;
  readonly logs: readonly PanelStatusLogEntryApiResponse[];
  readonly total: number;
  readonly hasMore: boolean;
}

/** イベントタイプをログレベルに変換 */
const mapEventTypeToLogLevel = (eventType: string): 'info' | 'warning' | 'error' => {
  switch (eventType) {
    case 'ERROR_OCCURRED':
      return 'error';
    case 'OPERATION_START':
    case 'OPERATION_END':
    case 'ERROR_RESOLVED':
    default:
      return 'info';
  }
};

/** ステータスログからメッセージを生成 */
const formatStatusLogMessage = (log: PanelStatusLogEntry): string => {
  const baseMessage = log.eventTypeDisplay;

  if (log.errorDetails !== null && log.errorDetails.length > 0) {
    return `${baseMessage}: ${log.errorDetails.join(', ')}`;
  }

  return baseMessage;
};

/** APIレスポンスをドメインモデルに変換 */
const mapApiResponseToStatusLogEntry = (
  apiLog: PanelStatusLogEntryApiResponse
): PanelStatusLogEntry => ({
  logId: apiLog.logId,
  panelId: apiLog.panelId,
  panelName: apiLog.panelName,
  previousStatus: apiLog.previousStatus,
  newStatus: apiLog.newStatus,
  eventType: apiLog.eventType,
  eventTypeDisplay: apiLog.eventTypeDisplay,
  errorDetails: apiLog.errorDetails,
  triggeredBy: apiLog.triggeredBy,
  createdAt: DateFactory.fromISOString(apiLog.createdAt),
});

/**
 * 最新のステータス変更ログを取得
 */
const getRecentStatusLogs = async (limit?: number): Promise<readonly PanelStatusLogEntry[]> => {
  try {
    const params = limit !== undefined ? { limit: String(limit) } : {};
    const response = await apiClient.get<RecentStatusLogsApiResponse>(
      '/api/dashboard/status-logs/recent',
      { params }
    );

    if (!response.data.success) {
      throw new Error('ステータス変更ログの取得に失敗しました');
    }

    return response.data.logs.map(mapApiResponseToStatusLogEntry);
  } catch (error) {
    logger.captureError(error, 'dashboardApiService.getRecentStatusLogs');
    // エラー時は空配列を返す（ダッシュボードの他の部分は表示継続）
    return [];
  }
};

/**
 * ステータス変更ログを取得（ページネーション対応）
 */
export const getStatusLogs = async (options?: {
  readonly limit?: number;
  readonly offset?: number;
  readonly panelId?: number;
}): Promise<PanelStatusLogData> => {
  try {
    const params: Record<string, string> = {};
    if (options?.limit !== undefined) {
      params['limit'] = String(options.limit);
    }
    if (options?.offset !== undefined) {
      params['offset'] = String(options.offset);
    }
    if (options?.panelId !== undefined) {
      params['panelId'] = String(options.panelId);
    }

    const response = await apiClient.get<StatusLogsApiResponse>(
      '/api/dashboard/status-logs',
      { params }
    );

    if (!response.data.success) {
      throw new Error('ステータス変更ログの取得に失敗しました');
    }

    return {
      logs: response.data.logs.map(mapApiResponseToStatusLogEntry),
      total: response.data.total,
      hasMore: response.data.hasMore,
    };
  } catch (error) {
    logger.captureError(error, 'dashboardApiService.getStatusLogs');
    throw error;
  }
};

/**
 * ダッシュボードデータを取得
 * @returns ダッシュボードデータ
 */
export const getDashboardData = async (): Promise<DashboardData> => {
  try {
    const response = await apiClient.get<DashboardApiResponse>('/api/dashboard');

    if (!response.data.success) {
      throw new Error('ダッシュボードデータの取得に失敗しました');
    }

    const data = response.data.data;

    // APIレスポンスをドメインモデルに変換
    const panels: PanelInfo[] = data.panelStatusGrid.panels.map(panel => {
      const lastCommunication = panel.lastCommunication ?? DateFactory.now().toISOString();
      return {
        panelId: PanelId.create(panel.panelId),
        panelName: panel.panelName,
        status: PanelStatus.create(panel.status),
        lastUpdated: DateFactory.fromISOString(lastCommunication)
      };
    });

    // ステータス変更ログを取得してシステムログとして表示
    const statusLogs = await getRecentStatusLogs(MAX_LOG_DISPLAY_COUNT);
    const systemLogs = statusLogs.map(log => ({
      logId: String(log.logId),
      timestamp: log.createdAt,
      level: LogLevel.create(mapEventTypeToLogLevel(log.eventType)),
      message: formatStatusLogMessage(log),
      source: log.panelName,
    }));

    return {
      statistics: data.statistics,
      panelStatusGrid: {
        panels,
        maxPanelCount: MAX_PANEL_DISPLAY_COUNT
      },
      systemLog: {
        logs: systemLogs,
        maxDisplayCount: MAX_LOG_DISPLAY_COUNT
      }
    };
  } catch (error) {
    logger.captureError(error, 'dashboardApiService.getDashboardData');
    throw error;
  }
};

/**
 * 統計情報のみを取得
 * @returns 統計情報
 */
export const getDashboardStatistics = async (): Promise<{
  readonly totalPanelCount: number;
  readonly activePanelCount: number;
  readonly stoppedPanelCount: number;
  readonly warningCount: number;
}> => {
  try {
    const response = await apiClient.get<StatisticsApiResponse>('/api/dashboard/statistics');

    if (!response.data.success) {
      throw new Error('統計情報の取得に失敗しました');
    }

    return response.data.statistics;
  } catch (error) {
    logger.captureError(error, 'dashboardApiService.getDashboardStatistics');
    throw error;
  }
};

/**
 * 盤ステータスグリッドのみを取得
 * @returns 盤ステータスグリッドデータ
 */
export const getPanelStatusGrid = async (): Promise<Array<{
  readonly panelId: number;
  readonly panelName: string;
  readonly status: 'active' | 'stopped' | 'warning';
  readonly lastCommunication: Date | null;
}>> => {
  try {
    const response = await apiClient.get<PanelStatusApiResponse>('/api/dashboard/panel-status');

    if (!response.data.success) {
      throw new Error('盤ステータスの取得に失敗しました');
    }

    return response.data.panels.map(panel => ({
      ...panel,
      lastCommunication: panel.lastCommunication ? DateFactory.fromISOString(panel.lastCommunication as string) : null
    }));
  } catch (error) {
    logger.captureError(error, 'dashboardApiService.getPanelStatusGrid');
    throw error;
  }
};

/**
 * システムログのみを取得
 * @returns システムログ
 */
export const getSystemLogs = async (): Promise<Array<{
  readonly logId: string;
  readonly timestamp: Date;
  readonly level: 'info' | 'warning' | 'error';
  readonly message: string;
  readonly source: string | null;
}>> => {
  try {
    const response = await apiClient.get<SystemLogsApiResponse>('/api/dashboard/logs');

    if (!response.data.success) {
      throw new Error('システムログの取得に失敗しました');
    }

    return response.data.logs.map(log => ({
      ...log,
      timestamp: DateFactory.fromISOString(log.timestamp as string)
    }));
  } catch (error) {
    logger.captureError(error, 'dashboardApiService.getSystemLogs');
    throw error;
  }
};

/**
 * シリアライズされたログエントリの型定義
 * JSON.parse(JSON.stringify())によってシリアライズされた後のデータ構造
 */
interface SerializedLogEntry {
  readonly logId: string;
  readonly timestamp: string;
  readonly level: string;
  readonly message: string;
  readonly source?: string;
}

/**
 * シリアライズされたシステムログデータの型定義
 */
interface SerializedSystemLogData {
  readonly logs: readonly SerializedLogEntry[];
  readonly maxDisplayCount: number;
}

/**
 * シリアライズされたログデータからドメインオブジェクトを復元
 * JSON.parse(JSON.stringify())によって失われた値オブジェクトを復元する
 * @param serializedData シリアライズされたログデータ
 * @returns 復元されたSystemLogEntry配列
 */
export const restoreSystemLogEntries = ({
  serializedData
}: {
  serializedData: SerializedSystemLogData
}): readonly SystemLogEntry[] => {
  return serializedData.logs.map((log) => ({
    logId: log.logId,
    timestamp: DateFactory.fromISOString(log.timestamp),
    level: LogLevel.create(log.level),
    message: log.message,
    source: log.source,
  }));
};